import { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTrackSwitchedSortType } from '@air/analytics';
import { SortDirection, ViewTypeName } from '@air/api/types';

import { getViewSortingMenuOptions } from '~/components/ViewControls/ViewSortingMenu/getViewSortingMenuOptions';
import {
  ViewSortingMenuComponent,
  type ViewSortingMenuComponentProps,
} from '~/components/ViewControls/ViewSortingMenu/ViewSortingMenuComponent';
import { RELEVANCE_SORT_FIELD_NAME } from '~/constants/relevanceSortFieldName';
import { useAppliedSortField } from '~/hooks/search/useAppliedSortField';
import { useClearSearchSortOverrideWhenQueryCleared } from '~/hooks/search/useClearSearchSortOverrideWhenQueryCleared';
import { useIsFlattenedView } from '~/hooks/search/useIsFlattenedView';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { useSearchParams } from '~/hooks/search/useSearchParams';
import { centralizedBoardExistsSelector } from '~/store/centralizedBoard/selectors';
import { setSearchSortOverrideAction, setSortingFieldAction } from '~/store/configViews/actions';
import {
  currentSortFieldNameSelector,
  currentViewTypeNameSelector,
  savedConfigurableViewsSelector,
  sortingConfigViewOptionsSelector,
} from '~/store/configViews/selectors';
import { getDefaultSortDirection, isRelevanceSortFieldName, isSortFieldWithDirection } from '~/store/configViews/utils';
import { useAirStore } from '~/utils/ReduxUtils';

export type ViewSortingMenuProps = {
  compact?: boolean;
};

export const ViewSortingMenu = memo(({ compact }: ViewSortingMenuProps) => {
  const dispatch = useDispatch();
  const store = useAirStore();
  const isInBoardPage = useSelector(centralizedBoardExistsSelector);
  const { isSearchActive } = useIsSearchActive();
  const { hasSearchParams } = useSearchParams();
  const { isFlattenedView } = useIsFlattenedView();
  const { trackSwitchedSortType } = useTrackSwitchedSortType();

  const sortingConfigViewOptions = useSelector(sortingConfigViewOptionsSelector);
  const currentSortFieldName = useSelector(currentSortFieldNameSelector);
  const { appliedSortFieldDirection, appliedSortFieldName } = useAppliedSortField();
  const currentViewTypeName = useSelector(currentViewTypeNameSelector);

  const allowCustomSorting = isInBoardPage && !isSearchActive && !isFlattenedView;
  const currentlySortedBy = appliedSortFieldName || '';

  const availableSortOptions = useMemo(
    () =>
      getViewSortingMenuOptions({
        allowCustomSorting,
        hasSearchParams,
        sortingFieldNames: sortingConfigViewOptions.map((option) => option.fieldName),
      }),
    [allowCustomSorting, hasSearchParams, sortingConfigViewOptions],
  );

  useClearSearchSortOverrideWhenQueryCleared();

  useEffect(() => {
    if (!allowCustomSorting && currentSortFieldName === 'customSort') {
      const savedConfigViews = savedConfigurableViewsSelector(store.getState());
      const currentViewType = currentViewTypeNameSelector(store.getState());

      // If custom sort is not available, try to switch to the sort field that is saved on this board
      // if cusrom sort is saved sort, set it to 'dateModified' desc (default for other views)
      const savedViewForViewType = savedConfigViews.find((view) => view.viewType.name === currentViewType);
      let defaultSortField = 'dateModified';
      let defaultSortDirection = SortDirection.desc;
      const savedSortField = savedViewForViewType?.sortFields[0];

      if (savedSortField && savedSortField.name !== 'customSort' && savedSortField.name) {
        defaultSortField = savedSortField.name;
        defaultSortDirection = savedSortField.direction;
      }

      dispatch(setSortingFieldAction({ sortingFieldName: defaultSortField, direction: defaultSortDirection }));
    }
  }, [allowCustomSorting, currentSortFieldName, dispatch, store]);

  const onSortChange: ViewSortingMenuComponentProps['onChange'] = useCallback(
    (sortingFieldName, direction) => {
      if (isRelevanceSortFieldName(sortingFieldName)) {
        dispatch(setSearchSortOverrideAction({ searchSortOverride: RELEVANCE_SORT_FIELD_NAME }));
        trackSwitchedSortType({ direction: SortDirection.desc, sortingFieldName });
        return;
      }

      const newDirection =
        sortingFieldName === currentlySortedBy ? direction : getDefaultSortDirection(sortingFieldName);
      dispatch(setSortingFieldAction({ sortingFieldName, direction: newDirection }));
      dispatch(setSearchSortOverrideAction({ searchSortOverride: null }));
      trackSwitchedSortType({ sortingFieldName, direction: newDirection });
    },
    [currentlySortedBy, dispatch, trackSwitchedSortType],
  );

  if (!availableSortOptions.length) {
    return null;
  }

  const isTableView = currentViewTypeName === ViewTypeName.table;

  return (
    <ViewSortingMenuComponent
      sortDirection={appliedSortFieldDirection}
      availableSortOptions={availableSortOptions}
      currentlySortedBy={currentlySortedBy}
      onChange={onSortChange}
      supportsDirection={isSortFieldWithDirection}
      // Table view forces the labeled trigger: the table's own column headers are what normally show the
      // sort field, so the header trigger must keep the field name for when they are not visible.
      compact={isTableView ? false : compact}
    />
  );
});

ViewSortingMenu.displayName = 'ViewSortingMenu';
